import { useInteraction } from 'Framework/View/Hooks/useInteraction.js'
import type { EItemType, IOrder, ISamplePool } from 'Model/index.js'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import type { State } from '../../../Flux/Slice/index.js'
import { getSampleMetaDatas } from '../../../Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import { getSamplePools } from '../../../Flux/Selector/Order/GetSamplePool/GetSamplePoolSelector.js'
import { getOrder } from '../../../Flux/Selector/Order/GetOrder/GetOrderSelector.js'
import { currentPool } from '../../../Flux/Selector/Order/CurrentOrderState.js'
import { RenderEvenlySpaced } from 'Util/WidgetsUtils.js'
import { FileInput } from 'View/Common/index.js'
import { SampleTable } from 'View/Component/SampleTable/SampleTable.js'
import type { Subject } from 'rxjs'
import { prevalidateFiles } from './FileHandling/PreValidator.js'
import type { OrderState } from '../../../Util/OrderState.js'
import { addMessage } from '../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage } from '../../../Model/Message/IMessage.js'
import { useUppy } from 'Provider/UppyProvider.tsx'
import { useConfirm } from '../../Component/ConfirmProvider.js'
import Actions from '../../../Flux/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import type { SampleConfigurationTypeKey } from '../../../Generated/SampleConfigurationType.js'
import { SampleConfigurationTypes } from '../../../Generated/SampleConfigurationType.js'
import type { DetectedSequencingPlatform } from '../../../Util/sequencingPlatformDetection.js'
import { consensusSequencingPlatform, detectSequencingPlatforms } from '../../../Util/sequencingPlatformDetection.js'
import {
  getSampleConfigurationMismatch,
  getSampleConfigurationMismatchMessage,
  getSampleConfigurationSwitchOptionLabel,
  isRecommendedSampleConfiguration,
  getSequencingPlatformLabel,
  sequencingPlatformDropzoneHint
} from '../../../Util/sequencingPlatform.js'
import { trackOrderAction } from '../../../Services/Telemetry/index.js'
import { PoolRenameControl, poolDisplayName } from './PoolName.js'
import { lacksReverseFile, planReversePairingByName, reverseFileCandidates } from '../../../Util/reverseFilePairing.js'

export type SamplePoolWidgetProps = {
  editable: boolean
  /* the label outlives `editable`: lab and customer may rename a pool after submission */
  renamable: boolean
  index: number
  metadataEditable: boolean
  samplePool: ISamplePool
  order: IOrder
  message$: Subject<{ type: 'success' | 'error' | 'warning' | 'info'; message: string }>
  orderState: OrderState
  allPools: ISamplePool[]
  onPoolDeleted: () => void
}

export const SamplePoolWidget = (props: SamplePoolWidgetProps) => {
  const { uppy, poolIsLoading, knownFileNames, files: uploadedFiles } = useUppy()
  const loading = poolIsLoading(props.samplePool.id)

  // How many files are still in flight for THIS pool, so we can show an explicit "uploading…"
  // indicator next to the file inputs (uploads run via autoProceed and the drawer may be closed).
  const [uploadingCount, setUploadingCount] = useState(0)
  useEffect(() => {
    if (!uppy) return
    const refresh = () =>
      setUploadingCount(
        uppy.getFiles().filter((f) => f.meta.poolId === props.samplePool.id && !f.progress?.uploadComplete).length
      )
    refresh()
    const events = ['file-added', 'file-removed', 'upload', 'upload-progress', 'upload-success', 'upload-error', 'complete'] as const
    events.forEach((e) => uppy.on(e, refresh))
    return () => events.forEach((e) => uppy.off(e, refresh))
  }, [uppy, props.samplePool.id])

  const { choose, confirm } = useConfirm()

  const dispatch = useDispatch()
  const store = useStore<typeof State>()
  /* for dialog callbacks, which run when the dialog closes: the pool's samples and metadata as they are then */
  const currentPoolMetadatas = () => {
    const state = store.getState()
    const samples = currentPool(getSamplePools(state), props.samplePool.id)?.samples ?? props.samplePool.samples
    const metadatas = getSampleMetaDatas(state)
    return samples.map((sample) => metadatas.find((x) => x.sampleId === sample.id)).filter((x) => !!x)
  }

  const poolItemCount = props.allPools.reduce((sum, pool) => sum + pool.samples.length + pool.negativeControls.length, 0)
  const pairedItemCount = props.allPools.reduce(
    (sum, pool) => sum + [...pool.samples, ...pool.negativeControls].filter((item) => !!item.reverseFileId).length,
    0
  )

  const missingReverse = [...props.samplePool.samples, ...props.samplePool.negativeControls].filter((item) =>
    lacksReverseFile(item, props.order.sampleConfiguration)
  )
  const orderUploads = useSelector((state: typeof State) => state.order.fileList)
  const uploadedInPool = uploadedFiles?.fileNamesInPool(props.samplePool.id).length ?? 0
  useEffect(() => {
    if (props.editable && missingReverse.length > 0) {
      dispatch(Actions[orderSlice.name].loadFiles({ orderId: props.order.id }))
    }
  }, [props.editable, missingReverse.length, uploadedInPool, props.order.id, dispatch])

  /** Pairs this pool's samples missing their R2 with uploads (or with the samples their R2s became) by file name. */
  const pairReverseFilesByName = async () => {
    const liveItems = props.allPools.flatMap((pool) => [...pool.samples, ...pool.negativeControls])
    const plan = planReversePairingByName(missingReverse, (target) =>
      reverseFileCandidates(target, orderUploads ?? [], liveItems, props.order.sampleConfiguration)
    )
    if (plan.length === 0) {
      await confirm(
        {
          title: 'No R2 files match by name',
          message:
            'None of the files uploaded to this order is named like the R2 of a sample missing one (e.g. sample_R1 / sample_R2). ' +
            'Drop the R2 files into this pool, or add each one from its row.',
          hideCancel: true,
          confirmLabel: 'Got it'
        },
        () => {}
      )
      return
    }
    const absorbing = plan.filter((p) => p.absorbSampleId).length
    await confirm(
      {
        title: `Add ${plan.length} R2 file${plan.length === 1 ? '' : 's'}?`,
        message: (
          <>
            {plan.map((p) => (
              <span key={p.sample.id} className="block font-mono text-xs">
                {p.sample.forwardFile?.fileName} ← {p.fileName}
                {p.absorbSampleId ? ' (its own sample now, removed)' : ''}
              </span>
            ))}
            {absorbing > 0 && (
              <span className="mt-2 block">
                {absorbing} of these R2 files are samples of their own; those samples are removed and their files become the R2s.
                Metadata stays with the R1 sample; an R2 sample&apos;s metadata moves over only where the R1 sample has none.
              </span>
            )}
          </>
        ),
        confirmLabel: 'Add R2 files'
      },
      () => {
        plan.forEach((p) =>
          dispatch(
            Actions[orderSlice.name].attachReverseFileRequest({
              orderId: props.order.id,
              poolId: p.sample.poolId ?? props.samplePool.id,
              sampleId: p.sample.id,
              reverseFileId: p.fileId,
              absorbSampleId: p.absorbSampleId
            })
          )
        )
      }
    )
  }

  /**
   * Offers — or, where the order-update path would refuse it, explains — the sample-format correction
   * the sniffed files call for, and answers with the configuration the user picked.
   *
   * Deliberately not part of pre-validation: that channel bounces files and says why, while this one
   * leaves the files alone and changes the order instead.
   */
  const promptSampleConfigurationSwitch = async (detected: DetectedSequencingPlatform): Promise<SampleConfigurationTypeKey | null> => {
    const mismatch = getSampleConfigurationMismatch({
      detected,
      current: props.order.sampleConfiguration,
      sampleCount: poolItemCount,
      pairedSampleCount: pairedItemCount,
      submitted: !!props.order.orderSubmission
    })
    if (!mismatch) return null

    const message = getSampleConfigurationMismatchMessage(mismatch)
    if (mismatch.blocker) {
      await confirm({ title: 'These files do not fit this order', message, hideCancel: true, confirmLabel: 'Got it' }, () => {})
      return null
    }

    const picked = await choose(
      { title: `Switch this order to ${getSequencingPlatformLabel(mismatch.detected)} samples?`, message },
      mismatch.targets.map((target, index) => ({
        label: getSampleConfigurationSwitchOptionLabel(target, mismatch.detected),
        testId: `switch-sample-configuration-${target.toLowerCase().replaceAll('_', '-')}`,
        className: isRecommendedSampleConfiguration(target, mismatch.detected) ? 'btn-primary' : '',
        value: index
      })),
      () => {}
    )
    const target = typeof picked === 'number' ? mismatch.targets[picked] : undefined
    if (!target) {
      return null
    }

    /* The prompt stayed open while the user decided: send the order as it is now, not as it was when the
       files were dropped, or this overwrites whatever changed meanwhile and carries a stale version. */
    const storedOrder = getOrder(store.getState())
    const order = storedOrder && storedOrder !== 'LOADING' && storedOrder.id === props.order.id ? storedOrder : props.order
    dispatch(
      Actions[orderSlice.name].updateOrderNameRequest({
        orderId: order.id,
        version: order.version || '0',
        name: order.name,
        description: order.description,
        taxa: order.targetTaxonomy,
        targetRegion: order.targetRegion,
        allowSingletons: order.allowSingletons,
        processingType: order.processingType,
        redlistMode: order.redlistMode ?? 'NONE',
        sampleConfiguration: target
      })
    )
    return target
  }

  async function handleFileInput(files: File[], itemType: EItemType) {
    if (!uppy) {
      dispatch(addMessage(errorMessage('Uploader is not initialized')))
      return
    }

    // Sniff before validating: an ONT file has no R1/R2 partner, and without its verdict the checks
    // below reject it for a designation it can never carry. The verdict rides along on the meta so
    // the sample records the platform its own file was read as.
    const platforms = await detectSequencingPlatforms(files)
    const switchedTo = await promptSampleConfigurationSwitch(consensusSequencingPlatform(platforms.values()))
    /* the switch was only dispatched, so the order prop still carries the configuration it replaces */
    const sampleConfiguration = switchedTo ?? props.order.sampleConfiguration
    const mappedFiles = files.map((file) => ({
      name: file.name,
      type: file.type,
      data: file,
      meta: {
        orderId: props.order.id,
        poolId: props.samplePool.id,
        itemType: itemType,
        // isMerged drives client-side file pairing; isTrimmed is sent for completeness but the
        // backend derives both from the order's sample configuration (the single source of truth).
        isTrimmed: SampleConfigurationTypes[sampleConfiguration]?.impliesTrimmed ?? false,
        isMerged: SampleConfigurationTypes[sampleConfiguration]?.impliesMerged ?? false,
        detectedSequencingPlatform: platforms.get(file) ?? 'UNKNOWN'
      }
    }))

    // Judge the selection against what this pool already holds, and only drop the files that are
    // really unusable: re-selecting everything after a partly-failed upload is the documented way to
    // fill the gaps, and that selection legitimately contains halves whose partner is already in S3.
    const { accepted, messages } = prevalidateFiles(mappedFiles, knownFileNames(props.samplePool.id))
    if (messages.length > 0) {
      dispatch(addMessage(errorMessage(messages.join('\n'))))
    }
    if (accepted.length === 0) return

    uppy.addFiles(accepted)
  }

  const sampleFiles$ = useInteraction<File[]>()
  const negativeControlFiles$ = useInteraction<File[]>()

  useEffect(() => {
    const filesSub = sampleFiles$.subscribe(async (value) => handleFileInput(value, 'SAMPLE'))
    return () => filesSub.unsubscribe()
  }, [sampleFiles$, props.samplePool])

  useEffect(() => {
    const filesSub = negativeControlFiles$.subscribe(async (value) => handleFileInput(value, 'NEGATIVE_CONTROL'))
    return () => filesSub.unsubscribe()
  }, [negativeControlFiles$, props.samplePool])

  return (
    <div className={'relative'}>
      {/* While the pool's completed-file list is being fetched, onBeforeFileAdded can't dedup and
          would reject files. Rather than a full-cover overlay (which forces cypress `force: true`
          and shifts layout), we disable the inputs below and show this compact, layout-neutral
          badge. Its data-testid lets tests wait for the pool to be ready before interacting. */}
      {loading && (
        <div
          data-testid="pool-loading"
          aria-live="polite"
          className="absolute top-1 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 rounded-full bg-base-100/90 px-3 py-1 text-sm text-base-content/70 shadow-sm"
        >
          <span className="loading loading-spinner loading-sm" aria-label="Loading pool…" />
          <span>Preparing pool…</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold truncate min-w-0" title={poolDisplayName(props.samplePool, props.index)}>
          {poolDisplayName(props.samplePool, props.index)}
        </h3>
        {!loading && (props.renamable || props.editable) && (
          <div className="flex items-center gap-2">
            {props.renamable && (
              <PoolRenameControl
                pool={props.samplePool}
                index={props.index}
                onRename={(label) => {
                  if (!props.samplePool.version) return
                  dispatch(
                    Actions[orderSlice.name].updateSamplePoolLabelRequest({
                      orderId: props.order.id,
                      poolId: props.samplePool.id,
                      label,
                      version: props.samplePool.version
                    })
                  )
                }}
              />
            )}
            {props.editable && (
              <div
                data-testid={'delete-pool'}
                className={'btn btn-sm btn-error'}
                onClick={() =>
                  choose(
                    {
                      title: 'Do you really want to delete this pool?',
                      message:
                        'This cannot be undone and will also remove all samples/negative controls within this pool. ' +
                        'To proceed, select what shall happen with sample metadata'
                    },
                    [
                      {
                        label: 'Delete Metadata',
                        testId: 'delete-metadata-choice',
                        className: 'btn-primary',
                        value: 1,
                        operation: () => {
                          currentPoolMetadatas().forEach((metadata) => {
                            dispatch(
                              Actions[orderSlice.name].deleteMetadataItemRequest({ orderId: props.order.id, metadataId: metadata.id })
                            )
                          })
                        }
                      },
                      {
                        label: 'Unlink from Samples',
                        testId: 'unlink-metadata-choice',
                        className: 'btn-primary',
                        value: 2,
                        operation: () => {
                          const unlinkDatas = currentPoolMetadatas()
                          if (unlinkDatas.length > 0) {
                            console.info('dispatch unlink')
                            dispatch(Actions[orderSlice.name].unlinkMetadatasRequest({ orderId: props.order.id, metadatas: unlinkDatas }))
                          }
                        }
                      }
                    ],
                    async () => {
                      /* all 'ok' options lead to deleting the pools */
                      trackOrderAction('delete_sample_pool', props.order)
                      dispatch(
                        Actions[orderSlice.name].deleteSamplePoolRequest({
                          orderId: props.order.id,
                          poolId: props.samplePool.id
                        })
                      )
                      props.onPoolDeleted()
                    }
                  )
                }
              >
                Delete Pool
              </div>
            )}
          </div>
        )}
      </div>
      <div className="divide-y border rounded-md bg-base-100 p-3 my-2">
        {[
          ['Number of Negative Controls', props.samplePool.negativeControls.length],
          ['Number of Samples', props.samplePool.samples.length]
        ].map((d) => (
          <RenderEvenlySpaced text={d[0]} value={d[1]} key={`${d[0]}${d[1]}`} />
        ))}
      </div>
      {props.editable && (
        <>
          <div className="flex flex-col md:flex-row gap-2 md:gap-4 mt-4">
            <FileInput
              testId="input-samples"
              onChange$={sampleFiles$}
              isMultiple={true}
              fileCounter={0}
              fileAccept=".gz"
              fileType="Sample Files (.fastq.gz)"
              infoTooltip={sequencingPlatformDropzoneHint}
              className="flex-1 md:mr-3"
              disabled={loading}
              setStarted={() => {}}
            />
            <FileInput
              testId="input-negative-controls"
              onChange$={negativeControlFiles$}
              isMultiple={true}
              fileCounter={0}
              fileAccept=".gz"
              fileType="Negative Control Files (.fastq.gz)"
              className="flex-1 md:ml-3"
              disabled={loading}
              setStarted={() => {}}
            />
          </div>
          {uploadingCount > 0 && (
            <div className="flex items-center gap-2 mt-2 text-sm text-base-content/70" data-testid="pool-upload-progress">
              <span className="loading loading-spinner loading-sm" />
              <span>
                Uploading {uploadingCount} file{uploadingCount === 1 ? '' : 's'}…
              </span>
            </div>
          )}
        </>
      )}
      {props.editable && missingReverse.length > 0 && (
        <div className="alert alert-warning mt-2 flex flex-wrap justify-between text-sm" data-testid="pool-missing-reverse">
          <span>
            {missingReverse.length} sample{missingReverse.length === 1 ? ' is' : 's are'} missing the R2 file. Drop the R2 files into this
            pool, add each one from its row, or pair them by file name.
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={!orderUploads}
            onClick={() => void pairReverseFilesByName()}
            data-testid="pair-reverse-files-by-name"
          >
            Pair R2 files by name
          </button>
        </div>
      )}
      <div className="border-t-2 mt-2 pt-2" />
      <>
        <div className="mt-4 overflow-x-auto">
          <SampleTable
            order={props.order}
            editable={props.editable}
            metadataEditable={props.metadataEditable}
            dataset={props.samplePool}
            isDone={props.orderState === 'DONE'}
            isMetadataTab={false}
            allPools={props.allPools}
          />
        </div>
      </>
    </div>
  )
}
